import {
  type QueryKey,
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { isHTTPError } from "ky";
import type {
  AdditionalIncomeInput,
  AddressInput,
  AppointmentInput,
  BudgetInput,
  DocumentUpdateInput,
  EmploymentInput,
  ReferenceInput,
  SubmitAnswerInput,
  VehiclePreferencesInput,
} from "@/lib/api.types";
import { isNetworkFailure } from "@/lib/server-errors";
import { consumerApi } from "./client";
import {
  additionalIncomesQueryOptions,
  addressesQueryOptions,
  appointmentsQueryOptions,
  documentsQueryOptions,
  employmentsQueryOptions,
  identificationCardsQueryOptions,
  plaidLinksQueryOptions,
  profileQueryOptions,
  questionSetQueryOptions,
  referencesQueryOptions,
  taskVehiclesQueryOptions,
  vehiclePreferencesQueryOptions,
} from "./reads";
import { type Fact, staleReadsAfter } from "./staleness";

// The failures ky used to retry for idempotent methods: no response at all, or one of these
// statuses. ky's own retry is off so it cannot multiply TanStack Query's, which now owns it.
const TRANSIENT_STATUSES = [408, 413, 429, 500, 502, 503, 504];

function isTransientFailure(error: Error): boolean {
  return isHTTPError(error)
    ? TRANSIENT_STATUSES.includes(error.response.status)
    : isNetworkFailure(error);
}

/** Retries once, and only a failure that is likely to clear on its own. */
const retryOnceIfTransient = (failureCount: number, error: Error) =>
  failureCount < 1 && isTransientFailure(error);

type WriteSpec<TData, TVariables> = {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** The read this write edits directly. */
  changes?: QueryKey;
  /** What else it changed; staleness.ts says which reads each fact makes stale. */
  facts?: readonly Fact[];
  /**
   * Where to write the response before invalidating, when it is the whole document. A failed
   * resnapping write refetches it instead, since the screen is showing a value the server rejected.
   */
  seeds?: QueryKey;
  /** Also mark reads stale when the write fails, for writes the screen has already acted on. */
  resnapOnError?: boolean;
  /** Writes sharing a scope id run one at a time, in order. */
  scope?: { id: string };
  /** Passed straight to the mutation; writes retry nothing unless they say so. */
  retry?: (failureCount: number, error: Error) => boolean;
};

function useWrite<TData, TVariables = void>(
  spec: WriteSpec<TData, TVariables>,
): UseMutationResult<TData, Error, TVariables> {
  const queryClient = useQueryClient();
  const invalidate = () => {
    for (const queryKey of staleReadsAfter(spec)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
  return useMutation({
    mutationFn: spec.mutationFn,
    scope: spec.scope,
    retry: spec.retry,
    // A successful response is always written first, so invalidating never refetches over it.
    onSuccess: async (data: TData) => {
      if (spec.seeds) {
        // A refetch already in flight, such as an earlier failed write's resnap, would answer
        // with the document from before this write and overwrite the seed.
        await queryClient.cancelQueries({ queryKey: spec.seeds });
        queryClient.setQueryData(spec.seeds, data);
      }
      if (!spec.resnapOnError) invalidate();
    },
    onSettled: (_data, error) => {
      if (!spec.resnapOnError) return;
      invalidate();
      if (error && spec.seeds) {
        void queryClient.invalidateQueries({ queryKey: spec.seeds });
      }
    },
  });
}

// Mutation hooks

export function useUpdateProfileMutation() {
  return useWrite({
    mutationFn: (data: Parameters<typeof consumerApi.updateProfile>[0]) =>
      consumerApi.updateProfile(data),
    changes: profileQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useCreateAddressMutation() {
  return useWrite({
    mutationFn: (data: AddressInput) => consumerApi.createAddress(data),
    changes: addressesQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useCreateEmploymentMutation() {
  return useWrite({
    mutationFn: (data: EmploymentInput) => consumerApi.createEmployment(data),
    changes: employmentsQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useCreateAdditionalIncomesMutation() {
  return useWrite({
    mutationFn: (data: AdditionalIncomeInput[]) =>
      consumerApi.createAdditionalIncomes(data),
    changes: additionalIncomesQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useDeleteAdditionalIncomeMutation() {
  return useWrite({
    mutationFn: async (id: string) => {
      try {
        return await consumerApi.deleteAdditionalIncome(id);
      } catch (error) {
        // A source deleted elsewhere (another tab or device) is already gone, as asked.
        if (isHTTPError(error) && error.response.status === 404) {
          return { success: true };
        }
        throw error;
      }
    },
    retry: retryOnceIfTransient,
    changes: additionalIncomesQueryOptions.queryKey,
    facts: ["applicant-data"],
    // The failure may mean the source is already gone; refetching shows what the server has.
    resnapOnError: true,
  });
}

export function useCreateReferenceMutation() {
  return useWrite({
    mutationFn: (data: ReferenceInput) => consumerApi.createReference(data),
    changes: referencesQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useCreateIdentificationCardMutation() {
  return useWrite({
    mutationFn: (data: FormData) => consumerApi.createIdentificationCard(data),
    changes: identificationCardsQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useCreateDocumentMutation() {
  return useWrite({
    mutationFn: (data: FormData) => consumerApi.createDocument(data),
    changes: documentsQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

// Deliberate behaviour change: this used to leave the task list alone. Every applicant-data
// write may close a Task, so it now marks tasks stale like the rest.
export function useUpdateDocumentMutation() {
  return useWrite({
    mutationFn: ({ id, data }: { id: string; data: DocumentUpdateInput }) =>
      consumerApi.updateDocument(id, data),
    changes: documentsQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useDeleteDocumentMutation() {
  return useWrite({
    mutationFn: async (id: string) => {
      try {
        return await consumerApi.deleteDocument(id);
      } catch (error) {
        // A document deleted elsewhere (another tab or device) is already in the state the
        // Applicant asked for, so there is nothing to report.
        if (isHTTPError(error) && error.response.status === 404) {
          return { success: true };
        }
        throw error;
      }
    },
    retry: retryOnceIfTransient,
    changes: documentsQueryOptions.queryKey,
    facts: ["applicant-data"],
    // The failure may mean the document is already gone; refetching shows what the server has.
    resnapOnError: true,
  });
}

export function useCreateAppointmentMutation() {
  return useWrite({
    mutationFn: (data: AppointmentInput) => consumerApi.createAppointment(data),
    changes: appointmentsQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useDeleteAppointmentMutation() {
  return useWrite({
    mutationFn: (id: string) => consumerApi.deleteAppointment(id),
    retry: retryOnceIfTransient,
    changes: appointmentsQueryOptions.queryKey,
    facts: ["applicant-data"],
    // The screen shows an error after a failure; refetching shows what the server has.
    resnapOnError: true,
  });
}

export function useCreatePlaidLinkMutation() {
  return useWrite({
    mutationFn: (data: {
      public_token: string;
      metadata: Record<string, unknown>;
    }) => consumerApi.createPlaidLink(data),
    changes: plaidLinksQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

// Deliberate behaviour change: this used to leave the task list alone. Every applicant-data
// write may close a Task, so it now marks tasks stale like the rest.
export function useReconnectPlaidLinkMutation() {
  return useWrite({
    mutationFn: (id: string) => consumerApi.reconnectPlaidLink(id),
    changes: plaidLinksQueryOptions.queryKey,
    facts: ["applicant-data"],
  });
}

export function useAcceptInviteMutation(slug: string) {
  return useWrite({
    mutationFn: (consents?: Parameters<typeof consumerApi.acceptInvite>[1]) =>
      consumerApi.acceptInvite(slug, consents),
  });
}

export function useSubmitAnswerMutation(questionSetId: string) {
  return useWrite({
    mutationFn: (data: SubmitAnswerInput) =>
      consumerApi.submitAnswer(questionSetId, data),
    changes: questionSetQueryOptions(questionSetId).queryKey,
    facts: ["applicant-data"],
  });
}

export function useSubmitQuestionSetMutation(questionSetId: string) {
  return useWrite({
    mutationFn: (answers: SubmitAnswerInput[]) =>
      consumerApi.submitQuestionSet(questionSetId, answers),
    changes: questionSetQueryOptions(questionSetId).queryKey,
    facts: ["applicant-data"],
  });
}

export function useUpdateVehiclePreferencesMutation() {
  return useWrite({
    mutationFn: (data: VehiclePreferencesInput) =>
      consumerApi.updateVehiclePreferences(data),
    changes: vehiclePreferencesQueryOptions.queryKey,
    facts: ["applicant-data", "vehicle-preferences"],
  });
}

// The budget sliders' write path, and the reason it is separate from
// useUpdateVehiclePreferencesMutation: that one PUTs the whole preference set, which is right for
// the preferences form and wrong for a control that saves on every release.
export function useUpdateBudgetMutation(taskId: string) {
  return useWrite({
    // Serialized, so a quick down-then-max adjustment can't land out of order and leave the grid
    // showing the older budget.
    scope: { id: `budget-${taskId}` },
    mutationFn: (data: BudgetInput) => consumerApi.updateBudget(data),
    // Seeded rather than invalidated on success: the response is the whole preferences document,
    // and the panel's "Save preferences" button builds its full payload from this cache entry.
    // On failure it is refetched instead (see resnapOnError below).
    seeds: vehiclePreferencesQueryOptions.queryKey,
    // Deliberately no "applicant-data" or "task" fact, so tasks aren't refetched: nothing about
    // the task changed, and this fires on every slider release while the task list is off screen.
    facts: ["vehicle-preferences"],
    // A rejected write leaves the sliders on a value the server never accepted; refetching
    // snaps them back to what it holds.
    resnapOnError: true,
  });
}

export function useDismissTaskMutation() {
  return useWrite({
    mutationFn: ({
      id,
      reason,
      note,
    }: {
      id: string;
      reason: string;
      note?: string;
    }) => consumerApi.dismissTask(id, { reason, note }),
    facts: ["task"],
  });
}

export function useReissueTaskMutation() {
  return useWrite({
    mutationFn: (id: string) => consumerApi.reissueTask(id),
    facts: ["task"],
  });
}

// Toggling only touches the grid; the task itself stays open until the applicant taps Done,
// so the task list is deliberately left alone until then. Deliberate behaviour change: the grid
// is also invalidated when a tap fails, so it resnaps to the server's truth.
export function useSelectVehicleMutation(taskId: string) {
  return useWrite({
    mutationFn: (vin: string) => consumerApi.selectVehicle(taskId, vin),
    changes: taskVehiclesQueryOptions(taskId).queryKey,
    resnapOnError: true,
  });
}

export function useDeselectVehicleMutation(taskId: string) {
  return useWrite({
    mutationFn: (vin: string) => consumerApi.deselectVehicle(taskId, vin),
    changes: taskVehiclesQueryOptions(taskId).queryKey,
    resnapOnError: true,
  });
}

export function useSubmitTaskVehiclesMutation(taskId: string) {
  return useWrite({
    mutationFn: () => consumerApi.submitTaskVehicles(taskId),
    changes: taskVehiclesQueryOptions(taskId).queryKey,
    facts: ["task"],
  });
}

export function useRespondToTaskMutation() {
  return useWrite({
    mutationFn: ({ id, choice }: { id: string; choice: string }) =>
      consumerApi.respondToTask(id, choice),
    facts: ["task"],
  });
}
